import { EntityType } from './types';
import { oauthService } from '../../services/oauthService';
import { ROLE_ADMIN, ROLE_USER } from '../../auth/roles';
import { getConfig } from '../../config/runtimeConfig';
import adminConfig from '../../config/fieldConfig.ADMIN.json';
import userConfig from '../../config/fieldConfig.USER.json';
import userStgConfig from '../../config/fieldConfig.USER.STG.json';
import readUserConfig from '../../config/fieldConfig.READ_USER.json';

/**
 * Configuration for disabled fields in the UpdateWizard
 * Loaded dynamically based on the current user's role and environment:
 *   ROLE_ADMIN         -> fieldConfig.ADMIN.json        (full access)
 *   ROLE_USER + stg    -> fieldConfig.USER.STG.json     (import/export + full view data access)
 *   ROLE_USER          -> fieldConfig.USER.json         (current default behaviour)
 *   (default)          -> fieldConfig.READ_USER.json    (everything blocked)
 */

export interface FieldConfig {
  [fieldName: string]: {
    disabled: boolean;
    reason?: string;
  };
}

export type EntityFieldConfig = {
  [key in EntityType]: FieldConfig;
};

export type NavigationConfig = FieldConfig;

// Map JSON entity keys to EntityType enum values
const KEY_MAPPING: Record<string, EntityType> = {
  product: EntityType.PRODUCT,
  entitlement: EntityType.ENTITLEMENT,
  org_entitlement: EntityType.ORG_ENTITLEMENT,
  sku_standalone: EntityType.SKU_STANDALONE,
  sku_suite: EntityType.SKU_SUITE,
  sku_addon: EntityType.SKU_ADDON,
  dependent_service: EntityType.DEPENDENT_SERVICE,
};

const parseConfig = (raw: Record<string, Record<string, unknown>>): EntityFieldConfig => {
  const config: Partial<EntityFieldConfig> = {};
  Object.entries(raw).forEach(([key, value]) => {
    const entityType = KEY_MAPPING[key];
    if (entityType) {
      config[entityType] = value as FieldConfig;
    }
  });
  return config as EntityFieldConfig;
};

const parseNavConfig = (raw: Record<string, Record<string, unknown>>): NavigationConfig => {
  return (raw['_navigation'] || {}) as NavigationConfig;
};

const ADMIN_CONFIG = parseConfig(adminConfig);
const USER_CONFIG = parseConfig(userConfig);
const USER_STG_CONFIG = parseConfig(userStgConfig);
const READ_USER_CONFIG = parseConfig(readUserConfig);

const ADMIN_NAV = parseNavConfig(adminConfig);
const USER_NAV = parseNavConfig(userConfig);
const USER_STG_NAV = parseNavConfig(userStgConfig);
const READ_USER_NAV = parseNavConfig(readUserConfig);

/** Returns true when the runtime environment is the stage environment. */
const isStageEnv = (): boolean => {
  try {
    const env = getConfig().env;
    return env === 'stg' || env === 'staging' || env === 'prod';
  } catch {
    // Config not yet loaded or running in a test context — treat as non-stage
    return false;
  }
};

/**
 * Resolve priority: ADMIN > USER (stg) > USER > default (READ_USER)
 */
const resolveConfig = (roles: string[]): EntityFieldConfig => {
  if (roles.includes(ROLE_ADMIN)) { return ADMIN_CONFIG; }
  if (roles.includes(ROLE_USER)) { return isStageEnv() ? USER_STG_CONFIG : USER_CONFIG; }
  // No recognised role – block everything by default
  return READ_USER_CONFIG;
};

const resolveNavConfig = (roles: string[]): NavigationConfig => {
  if (roles.includes(ROLE_ADMIN)) { return ADMIN_NAV; }
  if (roles.includes(ROLE_USER)) { return isStageEnv() ? USER_STG_NAV : USER_NAV; }
  return READ_USER_NAV;
};

/**
 * Returns the field config for the currently logged-in user.
 * Called at runtime so it always reflects the latest token.
 */
export const getFieldConfigForCurrentUser = (): EntityFieldConfig => {
  const roles = oauthService.getUserRoles();
  return resolveConfig(roles);
};

/**
 * Returns the field config for a specific active role.
 * Used by FieldConfigContext when RBAC role switching is active.
 */
export const getFieldConfigForRole = (activeRole: string): EntityFieldConfig => {
  return resolveConfig([activeRole]);
};

/**
 * Returns the navigation config for a specific active role.
 */
export const getNavConfigForRole = (activeRole: string): NavigationConfig => {
  return resolveNavConfig([activeRole]);
};

/**
 * Returns the navigation config for the currently logged-in user.
 */
export const getNavConfigForCurrentUser = (): NavigationConfig => {
  const roles = oauthService.getUserRoles();
  return resolveNavConfig(roles);
};
